import React, { useEffect, useState } from "react";
import "./TimesheetPage.css";
import TimesheetManager from "../managers/TimesheetManager";
import SupervisorManager from "../managers/SupervisorManager";
import Colors from "../styles/Colors.json";
import config from '../configLoader';
import { useSiteContext } from "../SiteContext";
import { 
  getGrandTotals, 
  formatTime, 
  applyOffsetToUTC, 
  applyOffsetToUTCDateTime,
  getWeekDay, 
  getDate, 
  getShortDate, 
  getTimeDiff,
  convertToLocalTime
} from "../util/DateAndTimeUtil";
import { TimeEntryType, TimesheetType, getPunchType, getTimeType } from "../interfaces/TimesheetInterface";
import PageWrapper from "../components/PageWrapper";
import {Day, EmployeeCustomerServiceWarranty, EmployeeOperationalTask, EmployeeSickClaim, EmployeeWorkOrderTask} from "../interfaces/EmployeeInterface";
import ActionButton from "../components/ActionButton";
import AuditManager from "../managers/AuditManager";
import AuditDetailModal from "../components/AuditDetailModal"; // Adjust path if needed
import {Audit} from "../interfaces/AuditInterface";
import {findIssues, getSickClaimList, getTotalAmount, getTotalAmountForWeek, getTotalProjectClaimedForDay, getTotalTaskCountForWeek, hasSickClaimsWithZeroAmount} from "../util/TaskUtil";
import {printTimesheet} from "../util/PrintUtil";
import {aggregateWeeklyProjectTasks} from "../util/weeklyTasks";
import TaskSetter from "../components/TaskSetter";
import ChangeSupervisorModal from "../components/ChangeSupervisorModal";
import UpdateMiscEmployeeTaskModal from "../components/UpdateMiscEmployeeTaskModal";
import DollarUtil from "../util/DollarUtil";
import GpsMapModal, { GpsMapPoint, GpsMapCircleWithLabel } from "../components/GpsMapModal";
import { hasValidGps, distanceMiles } from "../util/GpsUtil";
import bluePinPng from "../assets/marker-icon.png";
import redPin2xPng from "../assets/marker-icon-2x-red.png";

// Blue and red pins using local PNG assets (25 x 41)
const GpsPinIcon: React.FC<{ className?: string; isException?: boolean }> = ({
  className,
  isException,
}) => (
  <img
    src={isException ? redPin2xPng : bluePinPng}
    alt=""
    width={25}
    height={41}
    className={className}
    aria-hidden
  />
);

// Map icon for "Map" link buttons (open day/project map)
const MapIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden
  >
    <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
    <line x1="8" y1="2" x2="8" y2="18" />
    <line x1="16" y1="6" x2="16" y2="22" />
  </svg>
);

const TimesheetPage: React.FC = () => {
  const { setLoading, timesheetID, type, goBack, createPopup, translator, superAltID, superName, week, employee, payPeriodID, userID, userRole, payPeriods, setEmployee, setTimesheetID, setSuperID, setSuperAltID, setSuperName } = useSiteContext();
  const [viewMode, setViewMode] = useState<'hours' | 'tasks'>('hours');
  const [auditModalOpen, setAuditModalOpen] = useState(false);
  const [auditData, setAuditData] = useState<Audit[]>([]);
  const [sickClaims, setSickClaims] = useState<number[]>([]);
  const [changeSupervisorModalOpen, setChangeSupervisorModalOpen] = useState(false);
  const [selectedApprovals, setSelectedApprovals] = useState<number[]>([]);
  const [selectedSuperApprovals, setSelectedSuperApprovals] = useState<number[]>([]);
  const [selectedApprovalGroups, setSelectedApprovalGroups] = useState<number[]>([]);
  const [gpsMapOpen, setGpsMapOpen] = useState(false);
  const [gpsMapTitle, setGpsMapTitle] = useState("");
  const [gpsMapPoints, setGpsMapPoints] = useState<GpsMapPoint[]>([]);
  const [gpsMapCircles, setGpsMapCircles] = useState<GpsMapCircleWithLabel[]>([]);
  const [gpsMapShowRadiusLegend, setGpsMapShowRadiusLegend] = useState(false);
  const [gpsMapShowPunchList, setGpsMapShowPunchList] = useState(false);
  const [miscEditOpen, setMiscEditOpen] = useState(false);
  const [miscEditTarget, setMiscEditTarget] = useState<{
    miscEmployeeTaskID: number;
    initialAccountCodeID: number | null;
    initialMiscDescription: string;
  } | null>(null);

 const empName = employee.name.last + " - " + employee.name.first;
 const empID = employee.altEmployeeID ?? employee.employeeID;
 const jobTitle = employee.jobTitle ?? "";
 const days = employee.days;

 // Get the current pay period object
 const currentPayPeriod = payPeriods.find(pp => pp.PayPeriodID === payPeriodID);

   useEffect(() => {
   setSickClaims(getSickClaimList(days));
 }, [days]);

   // Initialize selected approvals based on user role and permissions
  useEffect(() => {
    if (employee.managerApprovals && type === TimesheetType.ManagerPending) {
      const initialSelected: number[] = [];
      
      employee.managerApprovals.forEach((approval: any, index: number) => {
        // Only consider pending approvals (those without approvalEmployee)
        if (!approval.approvalEmployee) {
          const shouldShowCheckbox = 
            userRole.admin || 
            userRole.payroll || 
            (userRole.supervisor && approval.requiredEmployee.adminEmployeeID === userID);
          
          if (shouldShowCheckbox && approval.requiredEmployee.adminEmployeeID === userID) {
            initialSelected.push(index);
          }
        }
      });
      
      setSelectedApprovals(initialSelected);
    }
  }, [employee.managerApprovals, type, userRole, userID]);

  // Initialize selected approval groups based on user membership
  useEffect(() => {
    if (employee.approvalGroups && type === TimesheetType.ManagerPending) {
      const initialSelected: number[] = [];

      employee.approvalGroups.forEach((group: any, index: number) => {
        const isMember = Array.isArray(group.adminEmployees) && group.adminEmployees.some((ae: any) => ae.adminEmployeeID === userID);
        // Follow same default-select logic: preselect if user is a member
        if (isMember) {
          initialSelected.push(index);
        }
      });

      setSelectedApprovalGroups(initialSelected);
    }
  }, [employee.approvalGroups, type, userRole, userID]);

  // Initialize selected supervisor approvals based on user role and permissions
  useEffect(() => {
    if (employee.superApprovals && (type === TimesheetType.Submitted || type === TimesheetType.SuperApproved || type === TimesheetType.PayApproved)) {
      const initialSelected: number[] = [];
      
      employee.superApprovals.forEach((approval: any, index: number) => {
        // Only consider pending approvals (those without approvalEmployee)
        if (!approval.approvalEmployee) {
          const shouldShowCheckbox = 
            userRole.admin || 
            userRole.payroll || 
            (userRole.supervisor && approval.requiredEmployee.adminEmployeeID === userID);
          
          if (shouldShowCheckbox && approval.requiredEmployee.adminEmployeeID === userID) {
            initialSelected.push(index);
          }
        }
      });
      
      setSelectedSuperApprovals(initialSelected);
    }
  }, [employee.superApprovals, type, userRole, userID]);



  if (!empName || !empID || days.length === 0) {
    return <p>Error: Missing employee information or time entries.</p>;
  }

  // Toggle between hours and tasks view
  const toggleViewMode = () => {
    setViewMode(viewMode === 'hours' ? 'tasks' : 'hours');
  };

  // Function to unlock the timesheet
  const onUnlock = async () => {
    setLoading(true);
    const id = parseInt(timesheetID!, 10);
    const adminID = userID;
    const unlock = await TimesheetManager.unlockTimesheet(id, adminID);
    if (unlock) {
      setLoading(false);
      createPopup(translator.translate("TimesheetUnlock"), translator.translate("Successful"));
      goBack();
    } else {
      createPopup(translator.translate("TimesheetUnlock"), translator.translate("FailedTryAgain"));
    }
    setLoading(false);
  };

  // Function to refresh the timesheet data
  const onRefresh = async () => {
    setLoading(true);
    try {
      let timesheetData;
      
      // Determine which API to call based on timesheet type
      if (type === TimesheetType.Unsubmitted) {
        timesheetData = await SupervisorManager.getUnsubmittedEmployeeTimesheet(
          employee.employeeID, 
          payPeriodID
        );
      } else {
        timesheetData = await SupervisorManager.getEmployeeTimesheet(
          employee.employeeID, 
          payPeriodID
        );
      }
      
      // Update the context with the full employee data including timesheet info
      setEmployee({
        ...employee,
        days: timesheetData.days || [],
        hourlyRate: timesheetData.hourlyRate,
        jobTitle: timesheetData.jobTitle,
        phoneNumber: timesheetData.phoneNumber,
        managerApprovals: timesheetData.managerApprovals || [],
        superApprovals: timesheetData.superApprovals || [],
        payrollApprovals: timesheetData.payrollApprovals || [],
        approvalGroups: timesheetData.approvalGroups || [],
      });

      if (timesheetData.timesheetID) { 
        setTimesheetID(timesheetData.timesheetID);
      } else {
        setTimesheetID(0);
      }
      
      // Set other required context values
      const supervisor = timesheetData.assignedSupervisor || timesheetData.timesheetSupervisor;

      setSuperID(supervisor.employeeID);
      setSuperAltID(supervisor.altEmployeeID || supervisor.employeeID.toString());
      setSuperName(`${supervisor.name.last} - ${supervisor.name.first}`);
    } catch (error) {
      console.error("Error refreshing timesheet:", error);
      createPopup("Refresh", "Failed to refresh timesheet data");
    } finally {
      setLoading(false);
    }
  };

           // Handle checkbox changes for approval selections
    const handleApprovalCheckboxChange = (index: number, checked: boolean) => {
      if (checked) {
        setSelectedApprovals(prev => [...prev, index]);
      } else {
        setSelectedApprovals(prev => prev.filter(i => i !== index));
      }
    };

    // Handle checkbox changes for supervisor approval selections
    const handleSuperApprovalCheckboxChange = (index: number, checked: boolean) => {
      if (checked) {
        setSelectedSuperApprovals(prev => [...prev, index]);
      } else {
        setSelectedSuperApprovals(prev => prev.filter(i => i !== index));
      }
    };

    // Handle checkbox changes for approval group selections
    const handleApprovalGroupCheckboxChange = (index: number, checked: boolean) => {
      if (checked) {
        setSelectedApprovalGroups(prev => [...prev, index]);
      } else {
        setSelectedApprovalGroups(prev => prev.filter(i => i !== index));
      }
    };

               // Function to approve the timesheet
     const onApprove = async () => {
      setLoading(true);
      const id = parseInt(timesheetID!, 10);
      const adminID = userID;
      
      try {
           if (type === TimesheetType.ManagerPending && (selectedApprovals.length > 0 || selectedApprovalGroups.length > 0)) {
           // Handle manager approvals - call one at a time for each selected checkbox
           for (const approvalIndex of selectedApprovals) {
             const approval = employee.managerApprovals?.[approvalIndex];
             if (approval && approval.requiredEmployee) {
               // Get the requiredManagerApprovalsID from the approval data
               const requiredManagerApprovalsID = approval.requiredEmployee.requiredManagersApprovalsID;
               
               const result = await TimesheetManager.approveManagerApproval(id, adminID, requiredManagerApprovalsID);
               if (!result) {
                 throw new Error(`Failed to approve manager approval for ${approval.requiredEmployee.name.first} ${approval.requiredEmployee.name.last}`);
               }
             }
           }
           
           // Handle approval groups - call one at a time for each selected group checkbox
           for (const groupIndex of selectedApprovalGroups) {
             const group: any = employee.approvalGroups?.[groupIndex];
             if (group) {
               // Support either requiredApprovalGroupsID or approvalGroupID from the payload
               const requiredApprovalGroupsID: number = group.requiredApprovalGroupsID ?? group.approvalGroupID;
               if (!requiredApprovalGroupsID) {
                 throw new Error("Missing requiredApprovalGroupsID for selected approval group");
               }
               const result = await TimesheetManager.approveApprovalGroup(id, adminID, requiredApprovalGroupsID);
               if (!result) {
                 throw new Error(`Failed to approve approval group ${group.name || ''}`);
               }
             }
           }
          
          createPopup(translator.translate("TimesheetApproval"), translator.translate("Successful"));
          goBack();
        } else if (type === TimesheetType.Submitted && selectedSuperApprovals.length > 0) {
          // Handle supervisor approvals - call one at a time for each selected checkbox
          for (const approvalIndex of selectedSuperApprovals) {
            const approval = employee.superApprovals?.[approvalIndex];
            if (approval && approval.requiredEmployee) {
              // Get the requiredSuperApprovalsID from the approval data
              const requiredSuperApprovalsID = approval.requiredEmployee.requiredSuperApprovalsID;
              
              const result = await TimesheetManager.approveSupervisorApproval(id, adminID, requiredSuperApprovalsID);
              if (!result) {
                throw new Error(`Failed to approve supervisor approval for ${approval.requiredEmployee.name.first} ${approval.requiredEmployee.name.last}`);
              }
            }
          }
         
         createPopup(translator.translate("TimesheetApproval"), translator.translate("Successful"));
         goBack();
        } else {
          // Handle regular timesheet approval
          const timesheetType = type!;
          const approve = await TimesheetManager.approveTimesheet(id, adminID, timesheetType);
          if (approve) {
            createPopup(translator.translate("TimesheetApproval"), translator.translate("Successful"));
            goBack();
          }
        }
      } catch (error) {
        console.error("Error during approval:", error);
        createPopup(translator.translate("TimesheetApproval"), translator.translate("FailedTryAgain"));
      } finally {
        setLoading(false);
      }
    };

  const onAudit = async () => {
    setLoading(true);
    const id = employee.employeeID.toString();
    const result = await AuditManager.getEmployeeAudit(id, payPeriodID);
    if (result && result.length > 0) {
      setAuditData(result);
      setAuditModalOpen(true);
    } else {
      createPopup("Audit", "No audit data available.");
    }
    setLoading(false);
  };  

  const onPrint = async () => {
    setLoading(true);
    const result = await printTimesheet(employee, payPeriodID, currentPayPeriod!);
    if(!result){
      createPopup("Timecard Print Failed!", "");
    } else { createPopup("Timecard Print Successful!", "Check downloads for the file."); }
    setLoading(false);
  };

  const { totalReg, totalOT, totalDT } = getGrandTotals(days);
  const infoStr = empName + " ";

  const hasAnySickClaims = days.some(
    (day: Day) => day.employeeSickClaims && day.employeeSickClaims.length > 0
  );

  // Rate payroll already saved on this timesheet (one rate per timesheet), so the panel can
  // show it next to the 90-day lookback suggestion. 0 when no rate has been entered yet.
  const savedSickRate = days.reduce(
    (max: number, day: Day) =>
      (day.employeeSickClaims ?? []).reduce((m, sc) => Math.max(m, sc.hourlyAmount ?? 0), max),
    0
  );

  function formatPhoneNumber(phoneNumber: string): string {
    if (!phoneNumber) return "-";
    if (phoneNumber === "+19999999999") return "-";

    const countryCode = phoneNumber.slice(0, 2);
    const areaCode = phoneNumber.slice(2, 5);
    const firstPart = phoneNumber.slice(5, 8);
    const secondPart = phoneNumber.slice(8, 12);
    return `${countryCode} (${areaCode}) ${firstPart}-${secondPart}`;
  }

  const canShowMiscUpdate =
    userRole.editMiscTaskAccountCodeAndDescription === true &&
    type !== TimesheetType.PayApproved &&
    !config.combineTasksInWeek;

  // Render project tasks for a day
  const renderProjectTasks = (projectEmployeeTasks: any[], showMiscUpdateButton: boolean) => {
    return projectEmployeeTasks.map((projectTask: any, projectIndex: number) => (
      <div key={projectIndex} className="project-entry">
        <div className="task-project-info">
          <strong>{projectTask.project.name} - {projectTask.project.altProjectID}</strong>
          <div>Total Claimed: {DollarUtil.formatDollar(Number(projectTask.totalAmountClaimed.toFixed(2)))}</div>
        </div>
        
        {/* Regular Tasks */}
        {projectTask.employeeTasks.length > 0 ?
        <>
          <div className="task-entries-container">
            {projectTask.employeeTasks.map((task: any, taskIndex: number) => (
              <div key={taskIndex} className="task-entry-card">
                <div className="task-info-row">
                  <strong>{task.task.taskInfo.name}</strong>
                  <span>Claimed: {DollarUtil.formatDollar(Number(task.amountClaimed.toFixed(2)))}</span>
                </div>
                <div className="task-details-row">
                  {task.task.taskInfo.description && (
                    <div>{task.task.taskInfo.description}</div>
                  )}
                  <div>Original: ${task.task.originalAmount}</div>
                  <div>Remaining: {DollarUtil.formatDollar(Number(task.task.remainingAmount.toFixed(2)))}</div>
                </div>
                {task.intermediateName && (
                  <div className="task-intermediate">
                    {task.intermediateName}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>:<></>
      }
        {/* Misc Tasks */}
        {projectTask.miscEmployeeTasks && projectTask.miscEmployeeTasks.length > 0 && (
          <div className="misc-entries-container">
            {projectTask.miscEmployeeTasks.map((miscTask: any, miscIndex: number) => (
              <div key={miscTask.miscEmployeeTaskID ?? miscIndex} className="misc-entry-card">
                <div className="misc-info-row">
                  <strong>Miscellaneous: {miscTask.miscDescription}</strong> 
                  <span>Claimed: {DollarUtil.formatDollar(Number(miscTask.amountClaimed.toFixed(2)))}</span>
                </div>
                <div className="misc-details-row">
                  {miscTask.accountCode?.name && (
                      <div>
                        Account: {miscTask.accountCode.name}
                        {miscTask.accountCode.altAccountCodeID != null &&
                        String(miscTask.accountCode.altAccountCodeID).trim() !== ""
                          ? ` (${String(miscTask.accountCode.altAccountCodeID).trim()})`
                          : ""}
                      </div>
                  )}
                  {miscTask.intermediateName && (
                    <div className="misc-intermediate">
                      {miscTask.intermediateName}
                    </div>
                  )}
                </div>
                {showMiscUpdateButton && typeof miscTask.miscEmployeeTaskID === "number" && (
                  <div style={{ marginTop: 8 }}>
                    <button
                      type="button"
                      className="gps-map-link-button"
                      onClick={() => {
                        setMiscEditTarget({
                          miscEmployeeTaskID: miscTask.miscEmployeeTaskID,
                          initialAccountCodeID: miscTask.accountCode?.accountCodeID ?? null,
                          initialMiscDescription: miscTask.miscDescription ?? "",
                        });
                        setMiscEditOpen(true);
                      }}
                    >
                      {translator.translate("UpdateMiscTaskButton")}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    ));
  };

  // Render sick claims for a day
  const renderSickClaims = (employeeSickClaims: EmployeeSickClaim[]) => {
    if (employeeSickClaims.length === 0) return null;

    return (
      <div className="entries-container">
        <div className="sick-entries-header">
          <strong>Sick Claims</strong>
          <span>
            Total:{" "}
            {formatTime(
              employeeSickClaims?.reduce(
                (sum: any, claim: { minutesClaimed: any }) => sum + claim.minutesClaimed,
                0
              ) ?? 0
            )}
          </span>
        </div>

        {employeeSickClaims.map((sickClaim: EmployeeSickClaim, sickClaimIndex: number) => {
          return (
            <div key={sickClaimIndex}>
              <div className="sick-entry-card">
                <div className="sick-info-row">
                  <strong>
                    {!sickClaim.description?.trim() ? "Sick Claim" : sickClaim.description}
                  </strong>
                  {!config.hideSickClaimHourlyRate && (
                    <span>Amount: {DollarUtil.formatDollar(sickClaim.amountClaimed)}</span>
                  )}
                  <div>Hours Claimed: {formatTime(sickClaim.minutesClaimed)}</div>
                </div>
                <div className="sick-details-row">
                  {sickClaim.accountCode && <div>Account: {sickClaim.accountCode.name}</div>}
                </div>
                {sickClaim.intermediateName && (
                  <div className="sick-intermediate">{sickClaim.intermediateName}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Render operational tasks for a day
  const renderOperationalTasks = (employeeOperationalTasks: EmployeeOperationalTask[]) => {
    if (employeeOperationalTasks.length === 0) return null;

    return (
      <div className="entries-container">
        <div className="operational-entries-header">
          <strong>Meetings / Other Tasks</strong>
          <span>Total: $
            {employeeOperationalTasks.reduce(
              (sum: number, task: EmployeeOperationalTask) =>
                sum + (task.amountClaimed ?? 0),
              0
            )}
          </span>
        </div>
      
        {employeeOperationalTasks.map((operationalTask: EmployeeOperationalTask, operationalTaskIndex: number) => (
          <div key={operationalTaskIndex}>
            <div className="operational-entry-card">
              <div className="operational-info-row">
                <span><strong>{operationalTask.operationalTask.name}</strong>: {operationalTask.description}</span>
                <span>Claimed: {DollarUtil.formatDollar(operationalTask.amountClaimed ?? 0)}</span>
              </div>
              <div className="operational-details-row">
                {operationalTask.operationalTask?.accountCode?.name && (
                  <div>Account: {operationalTask.operationalTask.accountCode.altAccountCodeID} - {operationalTask.operationalTask.accountCode.name}</div>
                )}
                {typeof operationalTask.originalAmount !== 'undefined' && (
                  <div>Original: {DollarUtil.formatDollar(operationalTask.originalAmount)}</div>
                )}
                {typeof operationalTask.remainingAmount !== 'undefined' && (
                  <div>Remaining: {DollarUtil.formatDollar(operationalTask.remainingAmount)}</div>
                )}
              </div>
              {operationalTask.intermediateName && (
                <div className="operational-intermediate">{operationalTask.intermediateName}</div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Render work order hour claims for a day (hours are display-only; dollars are implied)
  const renderWorkOrderTasks = (employeeWorkOrderTasks: EmployeeWorkOrderTask[]) => {
    if (employeeWorkOrderTasks.length === 0) return null;

    const totalHours = employeeWorkOrderTasks.reduce(
      (sum: number, task: EmployeeWorkOrderTask) => sum + (task.hoursClaimed ?? 0),
      0
    );
    const totalAmount = employeeWorkOrderTasks.reduce(
      (sum: number, task: EmployeeWorkOrderTask) => sum + (task.amountImplied ?? 0),
      0
    );

    return (
      <div className="entries-container">
        <div className="work-order-entries-header">
          <strong>Work Orders</strong>
          <span>Total: {totalHours} hrs / {DollarUtil.formatDollar(totalAmount)}</span>
        </div>

        {employeeWorkOrderTasks.map((workOrderTask: EmployeeWorkOrderTask, workOrderTaskIndex: number) => (
          <div key={workOrderTaskIndex}>
            <div className="work-order-entry-card">
              <div className="work-order-info-row">
                <span><strong>{workOrderTask.workOrderTask.name}</strong></span>
                <span>Claimed: {DollarUtil.formatDollar(workOrderTask.amountImplied ?? 0)}</span>
              </div>
              <div className="work-order-details-row">
                <div>Hours: {workOrderTask.hoursClaimed}</div>
                <div>Rate: {DollarUtil.formatDollar(workOrderTask.hourlyRate ?? workOrderTask.workOrderTask.laborPerHour)}/hr{workOrderTask.rateSource === "override" ? " (WO override)" : ""}</div>
              </div>
              {(workOrderTask.project?.altProjectID || workOrderTask.project?.name) && (
                <div className="work-order-intermediate">
                  {workOrderTask.project.altProjectID} - {workOrderTask.project.name}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Render customer service warranty tasks for a day
  const renderCustomerWarranties = (employeeCustomerServiceWarrantyTasks: EmployeeCustomerServiceWarranty[]) => {
    if (employeeCustomerServiceWarrantyTasks.length === 0) return null;

    return (
      <div className="entries-container">
        <div className="warranty-entries-header">
          <strong>Customer Service Warranty Tasks</strong>
          <span>Total: ${employeeCustomerServiceWarrantyTasks.reduce(
            (sum: number, task: EmployeeCustomerServiceWarranty) =>
              sum + (task.amountClaimed ?? 0),
            0
          )}
          </span>
        </div>

        {employeeCustomerServiceWarrantyTasks.map((warranty: EmployeeCustomerServiceWarranty, warrantyIndex: number) => (
          <div key={warrantyIndex}>
            <div className="warranty-entry-card">
              <div className="warranty-info-row">
                <span><strong>{warranty.customerServiceWarranty.name}</strong>: {warranty.description}</span>
                <span>Claimed: ${warranty.amountClaimed}</span>
              </div>
              {warranty.customerServiceWarranty.projectRequired && (
                <div className="warranty-project-row">
                  <span>Project: {warranty.project?.altProjectID} - {warranty.project?.name}</span>
                </div>
              )}
              <div className="warranty-details-row">
                {warranty.accountCode && <div>Account: {warranty.accountCode.name}</div>}
                <div>Location: {warranty.locationName}</div>
              </div>
              {warranty.intermediateName && (
                <div className="warranty-intermediate">{warranty.intermediateName}</div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Aggregate all project tasks from all days in the week. Lives in
  // util/weeklyTasks so the v2 review console's Weekly Tasks Summary rolls the
  // same rows up the same way (design_handoff_npm_timesheet_review §4).

  // Calculate the average hourly rate for the employee by taking the total amount for the week and dividing by the total piece hours for the week
  function getAvgHourlyRate(days: Day[]) {
    const totalPieceMinutes = days.reduce((acc: number, day: Day) => {
      const pieceMinutes = config.use2020PieceHourlyCalculation
        ? get2020PieceAndHourlyMinutesForDay(day).pieceMinutes
        : (day.totalTaskMinutes ?? 0);
      return acc + pieceMinutes;
    }, 0);
    const totalAmount = getTotalAmountForWeek(days);

    if (totalPieceMinutes === 0 || totalAmount === 0) {
      return "N/A";
    }

    return DollarUtil.formatDollar(Number(totalAmount / (totalPieceMinutes / 60)));
  }

  // Scroll to a specific day section
  const scrollToDay = (dayDate: string) => {
    const dayId = `day-${dayDate}`;
    const element = document.getElementById(dayId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Check for time gaps in a day by combining timeEntries and timeEntriesHourly, sorting by inDateTime, and checking for gaps
  function hasTimeGap(day: Day): boolean {
    const gapEntries = getTimeGapEntries(day);
    return gapEntries.size > 0;
  }

  // Get a Set of time identifiers that need highlighting (format: "identifier-in" or "identifier-out")
  function getTimeGapEntries(day: Day): Set<string> {
    const gapTimes = new Set<string>();
    
    // Combine all time entries with their identifiers
    const allEntries: Array<{ 
      inDateTime: string; 
      outDateTime: string | null;
      identifier: string; // Unique identifier for this entry
    }> = [];
    
    // Add regular time entries
    day.timeEntries.forEach((entry, index) => {
      allEntries.push({
        inDateTime: entry.inDateTime,
        outDateTime: entry.outDateTime || null,
        identifier: `timeEntry-${index}`
      });
    });
    
    // Add hourly project time entries
    if (day.hourlyProjects) {
      day.hourlyProjects.forEach((project, projectIndex) => {
        if (project.timeEntriesHourly) {
          project.timeEntriesHourly.forEach((entry, entryIndex) => {
            allEntries.push({
              inDateTime: entry.inDateTime,
              outDateTime: entry.outDateTime || null,
              identifier: `hourly-${projectIndex}-${entryIndex}`
            });
          });
        }
      });
    }
    
    // If there are no entries or only one entry, no gap is possible
    if (allEntries.length <= 1) {
      return gapTimes;
    }
    
    // Sort by inDateTime
    allEntries.sort((a, b) => {
      const dateA = new Date(a.inDateTime).getTime();
      const dateB = new Date(b.inDateTime).getTime();
      return dateA - dateB;
    });
    
    // Check for gaps between consecutive entries
    for (let i = 0; i < allEntries.length - 1; i++) {
      const currentEntry = allEntries[i];
      const nextEntry = allEntries[i + 1];
      
      // Skip if current entry doesn't have an outDateTime (open entry)
      if (!currentEntry.outDateTime) {
        continue;
      }
      
      // Compare outDateTime of current entry with inDateTime of next entry
      const currentOut = new Date(currentEntry.outDateTime).getTime();
      const nextIn = new Date(nextEntry.inDateTime).getTime();
      
      // If there's a gap (outDateTime != next inDateTime), mark the "Out" of current and "In" of next
      if (currentOut !== nextIn) {
        gapTimes.add(`${currentEntry.identifier}-out`);
        gapTimes.add(`${nextEntry.identifier}-in`);
      }
    }
    
    return gapTimes;
  }

  /** For 2020 config: compute Piece and Hourly (Job) minutes from hourlyProjects. Reg time with no project account → Piece; with account → Hourly (Job). */
  function get2020PieceAndHourlyMinutesForDay(day: any): { pieceMinutes: number; hourlyJobMinutes: number } {
    let pieceMinutes = 0;
    let hourlyJobMinutes = 0;
    if (!day.hourlyProjects || day.hourlyProjects.length === 0) {
      return { pieceMinutes, hourlyJobMinutes };
    }
    for (const hp of day.hourlyProjects) {
      let projectRegMinutes = 0;
      hp.timeEntriesHourly?.forEach((entry: any) => {
        if (entry.type !== TimeEntryType.Regular) return;
        if (!entry.outDateTime) return;
        const localIn = convertToLocalTime(entry.inDateTime, entry.inDateTimeOffset ?? "0:0");
        const localOut = convertToLocalTime(entry.outDateTime, entry.outDateTimeOffset ?? "0:0");
        const minutes = Math.round((localOut.getTime() - localIn.getTime()) / 60000);
        if (minutes > 0) projectRegMinutes += minutes;
      });
      const hasAccount = hp.account && hp.account.accountCodeID !== 0 && hp.account.accountCodeID !== undefined && hp.account.accountCodeID !== null;
      if (hasAccount) {
        hourlyJobMinutes += projectRegMinutes;
      } else {
        pieceMinutes += projectRegMinutes;
      }
    }
    return { pieceMinutes, hourlyJobMinutes };
  }

  function entryHasValidGps(entry: { inLatitude?: number; inLongitude?: number; outLatitude?: number; outLongitude?: number }): boolean {
    return hasValidGps(entry.inLatitude, entry.inLongitude) || hasValidGps(entry.outLatitude, entry.outLongitude);
  }

  function dayHasValidGps(day: Day): boolean {
    if (day.timeEntries?.some((e: any) => entryHasValidGps(e))) return true;
    if (day.hourlyProjects) {
      for (const hp of day.hourlyProjects) {
        if (hp.timeEntriesHourly?.some((e: any) => entryHasValidGps(e))) return true;
      }
    }
    return false;
  }

  /** Open map for a single entry. Pass 'in' or 'out' to show only that punch. Pass projectSite when opening from a project row to show project location and radius buffer on the map when the project has them. circleLabel is shown when clicking the buffer (e.g. Project name - AltProjectID). */
  function openEntryMap(
    entry: any,
    entryLabel: string,
    punch?: "in" | "out",
    projectSite?: { latitude: number; longitude: number; radiusMiles: number } | null,
    circleLabel?: string
  ) {
    const points: GpsMapPoint[] = [];
    const hasProjectSite =
      projectSite &&
      hasValidGps(projectSite.latitude, projectSite.longitude) &&
      typeof projectSite.radiusMiles === "number" &&
      projectSite.radiusMiles > 0;

    const showIn = punch !== "out" && hasValidGps(entry.inLatitude, entry.inLongitude);
    const showOut = punch !== "in" && hasValidGps(entry.outLatitude, entry.outLongitude);
    if (showIn) {
      const inRadius =
        hasProjectSite && projectSite
          ? distanceMiles(projectSite.latitude, projectSite.longitude, entry.inLatitude!, entry.inLongitude!) <= projectSite.radiusMiles
          : undefined;
      points.push({
        lat: entry.inLatitude!,
        lng: entry.inLongitude!,
        label: `In ${applyOffsetToUTC(entry.inDateTime, entry.inDateTimeOffset)}`,
        isException: !!entry.gpsException?.inException,
        exceptionMessage: entry.gpsException?.inExceptionMessage,
        inRadius,
      });
    }
    if (showOut) {
      const inRadius =
        hasProjectSite && projectSite
          ? distanceMiles(projectSite.latitude, projectSite.longitude, entry.outLatitude!, entry.outLongitude!) <= projectSite.radiusMiles
          : undefined;
      points.push({
        lat: entry.outLatitude!,
        lng: entry.outLongitude!,
        label: `Out ${entry.outDateTime ? applyOffsetToUTC(entry.outDateTime, entry.outDateTimeOffset) : ""}`,
        isException: !!entry.gpsException?.outException,
        exceptionMessage: entry.gpsException?.outExceptionMessage,
        inRadius,
      });
    }
    if (points.length === 0) return;
    setGpsMapTitle(entryLabel);
    setGpsMapPoints(points);
    setGpsMapCircles(
      hasProjectSite && projectSite
        ? [
            {
              lat: projectSite.latitude,
              lng: projectSite.longitude,
              radiusMiles: projectSite.radiusMiles,
              label: circleLabel?.trim() || undefined,
            },
          ]
        : []
    );
    setGpsMapShowRadiusLegend(!!hasProjectSite);
    setGpsMapShowPunchList(false);
    setGpsMapOpen(true);
  }

  function openDayMap(day: Day) {
    const points: GpsMapPoint[] = [];
    day.timeEntries?.forEach((entry: any) => {
      if (hasValidGps(entry.inLatitude, entry.inLongitude)) {
        points.push({
          lat: entry.inLatitude!,
          lng: entry.inLongitude!,
          label: `In ${applyOffsetToUTC(entry.inDateTime, entry.inDateTimeOffset)}`,
          isException: !!entry.gpsException?.inException,
          exceptionMessage: entry.gpsException?.inExceptionMessage,
          sortKey: entry.inDateTime ?? "",
          projectLabel: "",
        });
      }
      if (hasValidGps(entry.outLatitude, entry.outLongitude)) {
        points.push({
          lat: entry.outLatitude!,
          lng: entry.outLongitude!,
          label: `Out ${entry.outDateTime ? applyOffsetToUTC(entry.outDateTime, entry.outDateTimeOffset) : ""}`,
          isException: !!entry.gpsException?.outException,
          exceptionMessage: entry.gpsException?.outExceptionMessage,
          sortKey: entry.outDateTime ?? "",
          projectLabel: "",
        });
      }
    });
    day.hourlyProjects?.forEach((hp: any) => {
      const projectLabel = hp.project
        ? `${hp.project.name ?? ""}${hp.project.altProjectID ? ` - ${hp.project.altProjectID}` : ""}`.trim()
        : "";
      hp.timeEntriesHourly?.forEach((entry: any) => {
        if (hasValidGps(entry.inLatitude, entry.inLongitude)) {
          points.push({
            lat: entry.inLatitude!,
            lng: entry.inLongitude!,
            label: `In ${applyOffsetToUTC(entry.inDateTime, entry.inDateTimeOffset)}`,
            isException: !!entry.gpsException?.inException,
            exceptionMessage: entry.gpsException?.inExceptionMessage,
            sortKey: entry.inDateTime ?? "",
            projectLabel,
          });
        }
        if (hasValidGps(entry.outLatitude, entry.outLongitude)) {
          points.push({
            lat: entry.outLatitude!,
            lng: entry.outLongitude!,
            label: `Out ${entry.outDateTime ? applyOffsetToUTC(entry.outDateTime, entry.outDateTimeOffset) : ""}`,
            isException: !!entry.gpsException?.outException,
            exceptionMessage: entry.gpsException?.outExceptionMessage,
            sortKey: entry.outDateTime ?? "",
            projectLabel,
          });
        }
      });
    });
    if (points.length === 0) return;
    points.sort((a, b) => (a.sortKey ?? "").localeCompare(b.sortKey ?? ""));
    const dayCirclesMap = new Map<string, GpsMapCircleWithLabel>();
    day.hourlyProjects?.forEach((hp: any) => {
      const proj = hp.project;
      if (
        proj &&
        hasValidGps(proj.latitude, proj.longitude) &&
        typeof proj.radiusMiles === "number" &&
        proj.radiusMiles > 0
      ) {
        const key = `${proj.latitude},${proj.longitude},${proj.radiusMiles}`;
        if (!dayCirclesMap.has(key)) {
          const label = `${proj.name ?? ""}${proj.altProjectID ? ` - ${proj.altProjectID}` : ""}`.trim();
          dayCirclesMap.set(key, {
            lat: proj.latitude,
            lng: proj.longitude,
            radiusMiles: proj.radiusMiles,
            label: label || undefined,
          });
        }
      }
    });
    const dayCircles = Array.from(dayCirclesMap.values());
    setGpsMapTitle(`Punches - ${getWeekDay(day.date)} ${getDate(day.date)}`);
    setGpsMapPoints(points);
    setGpsMapCircles(dayCircles);
    setGpsMapShowRadiusLegend(false);
    setGpsMapShowPunchList(true);
    setGpsMapOpen(true);
  }

  function openProjectMap(project: any, day: Day) {
    const proj = project.project;
    const lat = proj?.latitude;
    const lng = proj?.longitude;
    const radiusMiles = proj?.radiusMiles;
    const hasProjectLocation = hasValidGps(lat, lng) && typeof radiusMiles === "number" && radiusMiles > 0;
    const projectLabel = proj ? `${proj.name ?? ""}${proj.altProjectID ? ` - ${proj.altProjectID}` : ""}`.trim() : "";
    const points: GpsMapPoint[] = [];
    if (hasProjectLocation) {
      project.timeEntriesHourly?.forEach((entry: any) => {
        if (hasValidGps(entry.inLatitude, entry.inLongitude)) {
          const inRadius = distanceMiles(lat!, lng!, entry.inLatitude!, entry.inLongitude!) <= radiusMiles;
          points.push({
            lat: entry.inLatitude!,
            lng: entry.inLongitude!,
            label: `In ${applyOffsetToUTC(entry.inDateTime, entry.inDateTimeOffset)}`,
            isException: !!entry.gpsException?.inException,
            exceptionMessage: entry.gpsException?.inExceptionMessage,
            inRadius,
            sortKey: entry.inDateTime ?? "",
            projectLabel,
          });
        }
        if (hasValidGps(entry.outLatitude, entry.outLongitude)) {
          const inRadius = distanceMiles(lat!, lng!, entry.outLatitude!, entry.outLongitude!) <= radiusMiles;
          points.push({
            lat: entry.outLatitude!,
            lng: entry.outLongitude!,
            label: `Out ${entry.outDateTime ? applyOffsetToUTC(entry.outDateTime, entry.outDateTimeOffset) : ""}`,
            isException: !!entry.gpsException?.outException,
            exceptionMessage: entry.gpsException?.outExceptionMessage,
            inRadius,
            sortKey: entry.outDateTime ?? "",
            projectLabel,
          });
        }
      });
    }
    points.sort((a, b) => (a.sortKey ?? "").localeCompare(b.sortKey ?? ""));
    setGpsMapTitle(proj ? `${proj.name ?? ""}${proj.altProjectID ? ` - ${proj.altProjectID}` : ""}`.trim() || "Project map" : "Project map");
    setGpsMapPoints(points);
    setGpsMapCircles(
      hasProjectLocation
        ? [{ lat: lat!, lng: lng!, radiusMiles, label: projectLabel || undefined }]
        : []
    );
    setGpsMapShowRadiusLegend(hasProjectLocation);
    setGpsMapShowPunchList(true);
    setGpsMapOpen(true);
  }

    return (
    <PageWrapper title={translator.translate("EmployeePunches")} backArrow={true} backgroundColor={Colors.White}>
      {/* Refresh Button Section */}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <button 
          onClick={onRefresh}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '10px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          title="Refresh"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ color: '#000' }}
          >
            <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
          </svg>
        </button>
      </div>

             {/* Required Approvals Section - Show for ManagerPending with approve button, or other types with managerApprovals for display only */}
       {(type === TimesheetType.ManagerPending || 
         ((type === TimesheetType.Submitted || type === TimesheetType.SuperApproved || type === TimesheetType.PayApproved) && 
          ((employee.managerApprovals && employee.managerApprovals.length > 0) || (employee.approvalGroups && employee.approvalGroups.length > 0)))) && (
        <div className="approval-section" style={{
          backgroundColor: Colors.White,
          border: `1px solid ${Colors.BorderGray}`,
          borderRadius: '8px',
          padding: '20px',
          margin: '20px 0',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '15px'
          }}>
                         <h3 style={{
               margin: '0',
               color: Colors.PrimaryText,
               fontSize: '18px',
               fontWeight: '600'
             }}>
               Required Manager Approvals
             </h3>
             {type === TimesheetType.ManagerPending && (
               <ActionButton
                 title={translator.translate("Approve")}
                  isEnabled={selectedApprovals.length > 0 || selectedApprovalGroups.length > 0}
                 onAction={onApprove}
               />
             )}
          </div>
                          {employee.managerApprovals && employee.managerApprovals.map((approval: any, index: number) => {
                          // Check if this is an approved approval (has approvalEmployee) or pending (has requiredEmployee)
                          const isApproved = approval.approvalEmployee !== undefined;
                          
                          let employeeName = '';
                          let approvalInfo: string | JSX.Element = '';
                          let shouldShowCheckbox = false;
                          
                                                     if (isApproved) {
                             // This is an approved approval
                             const requiredEmployeeName = `${approval.requiredEmployee.name.first} ${approval.requiredEmployee.name.last}`;
                             const approvalEmployeeName = `${approval.approvalEmployee.name.first} ${approval.approvalEmployee.name.last}`;
                             const formattedDateTime = applyOffsetToUTCDateTime(
                               approval.approvalEmployee.approvalDateTime, 
                               approval.approvalEmployee.approvalDateTimeOffset
                             );
                             approvalInfo = (
                               <span>
                                 <strong>{requiredEmployeeName}</strong> - Approved by <strong>{approvalEmployeeName}</strong> at <strong>{formattedDateTime}</strong>
                               </span>
                             );
                           } else {
                            // This is a pending approval
                            employeeName = `${approval.requiredEmployee.name.first} ${approval.requiredEmployee.name.last}`;
                            approvalInfo = <strong>{employeeName}</strong>;
                            
                            // Determine if checkbox should be shown based on user role
                            shouldShowCheckbox = 
                              userRole.admin || 
                              userRole.payroll || 
                              (userRole.hr && approval.requiredEmployee.adminEmployeeID === userID) ||
                              (userRole.supervisor && approval.requiredEmployee.adminEmployeeID === userID);
                          }
                          
                          return (
                            <div key={index} style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              padding: '10px 0'
                            }}>
                              {isApproved ? (
                                <span style={{
                                  color: Colors.Warranty,
                                  fontSize: '20px',
                                  fontWeight: 'bold',
                                  width: '20px'
                                }}>
                                  ✓
                                </span>
                              ) : shouldShowCheckbox ? (
                                <input
                                  type="checkbox"
                                  checked={selectedApprovals.includes(index)}
                                  onChange={(e) => handleApprovalCheckboxChange(index, e.target.checked)}
                                  style={{
                                    width: '20px',
                                    height: '20px',
                                    cursor: 'pointer'
                                  }}
                                />
                              ) : (
                                <div style={{
                                  width: '20px',
                                  height: '20px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  {/* Empty space to align with checkmark above */}
                                </div>
                              )}
                              <span style={{
                                fontSize: '16px',
                                color: Colors.PrimaryText
                              }}>
                                {approvalInfo}
                              </span>
                            </div>
                          );
                        })}

                        {/* Render approval groups under Required Manager Approvals for all relevant types
                            - ManagerPending: show checkboxes (pending only) and allow selection
                            - Submitted/SuperApproved/PayApproved: display only (no checkboxes) */}
                        {(type === TimesheetType.ManagerPending || type === TimesheetType.Submitted || type === TimesheetType.SuperApproved || type === TimesheetType.PayApproved) &&
                         employee.approvalGroups && employee.approvalGroups.length > 0 && (
                          <div>
                            {employee.approvalGroups.map((group: any, gIndex: number) => {
                              const groupName: string = group.name || 'Approval Group';
                              const adminEmployees: any[] = Array.isArray(group.adminEmployees) ? group.adminEmployees : [];
                              const isMember: boolean = adminEmployees.some((ae: any) => ae && ae.adminEmployeeID === userID);
                              const isApproved: boolean = !!group.approvalEmployee;

                              // Checkbox visibility follows same logic as managerApprovals pending:
                              // Admin/Payroll: can check; Supervisor: only if user is in the group
                              const canShowCheckboxForType = type === TimesheetType.ManagerPending;
                              const shouldShowCheckbox = canShowCheckboxForType && !isApproved && (userRole.admin || userRole.payroll || (userRole.hr && isMember) || (userRole.supervisor && isMember));

                              let groupInfo: string | JSX.Element = <strong>{groupName}</strong>;
                              if (isApproved && group.approvalEmployee && group.approvalEmployee.name) {
                                const approvalEmployeeName = `${group.approvalEmployee.name.first} ${group.approvalEmployee.name.last}`;
                                const formattedDateTime = applyOffsetToUTCDateTime(
                                  group.approvalEmployee.approvalDateTime,
                                  group.approvalEmployee.approvalDateTimeOffset
                                );
                                groupInfo = (
                                  <span>
                                    <strong>{groupName}</strong> - Approved by <strong>{approvalEmployeeName}</strong> at <strong>{formattedDateTime}</strong>
                                  </span>
                                );
                              }

                              return (
                                <div key={`group-${gIndex}`} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0' }}>
                                  {isApproved ? (
                                    <span style={{ color: Colors.Warranty, fontSize: '20px', fontWeight: 'bold', width: '20px' }}>✓</span>
                                  ) : shouldShowCheckbox ? (
                                    <input
                                      type="checkbox"
                                      checked={selectedApprovalGroups.includes(gIndex)}
                                      onChange={(e) => handleApprovalGroupCheckboxChange(gIndex, e.target.checked)}
                                      style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                                    />
                                  ) : (
                                    <div style={{ width: '20px', height: '20px' }}></div>
                                  )}
                                  <span style={{ fontSize: '16px', color: Colors.PrimaryText }}>
                                    {groupInfo}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
        </div>
      )}

             {/* Required Super Approvals Section - Show for Submitted, SuperApproved, and PayApproved with superApprovals for display only */}
       {((type === TimesheetType.Submitted || type === TimesheetType.SuperApproved || type === TimesheetType.PayApproved) && 
         employee.superApprovals && employee.superApprovals.length > 0) && (
         <div className="approval-section" style={{
           backgroundColor: Colors.White,
           border: `1px solid ${Colors.BorderGray}`,
           borderRadius: '8px',
           padding: '20px',
           margin: '20px 0',
           boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
         }}>
           <div style={{
             display: 'flex',
             justifyContent: 'space-between',
             alignItems: 'center',
             marginBottom: '15px'
           }}>
             <h3 style={{
               margin: '0',
               color: Colors.PrimaryText,
               fontSize: '18px',
               fontWeight: '600'
             }}>
               Required Super Approvals
             </h3>
             {type === TimesheetType.Submitted && (
               <ActionButton
                 title={translator.translate("Approve")}
                 isEnabled={selectedSuperApprovals.length > 0}
                 onAction={onApprove}
               />
             )}
           </div>
                     {employee.superApprovals && employee.superApprovals.map((approval: any, index: number) => {
             // Check if this is an approved approval (has approvalEmployee) or pending (has requiredEmployee)
             const isApproved = approval.approvalEmployee !== undefined;
             
             let employeeName = '';
             let approvalInfo: string | JSX.Element = '';
             let shouldShowCheckbox = false;
             
                           if (isApproved && approval.approvalEmployee && approval.approvalEmployee.name) {
                // This is an approved approval
                const approvalEmployeeName = `${approval.approvalEmployee.name.first} ${approval.approvalEmployee.name.last}`;
                const formattedDateTime = applyOffsetToUTCDateTime(
                  approval.approvalEmployee.approvalDateTime, 
                  approval.approvalEmployee.approvalDateTimeOffset
                );
                
                if (approval.requiredEmployee && approval.requiredEmployee.name) {
                  // Show both required employee and approval employee
                  const requiredEmployeeName = `${approval.requiredEmployee.name.first} ${approval.requiredEmployee.name.last}`;
                  approvalInfo = (
                    <span>
                      <strong>{requiredEmployeeName}</strong> - Approved by <strong>{approvalEmployeeName}</strong> at <strong>{formattedDateTime}</strong>
                    </span>
                  );
                } else {
                  // Show only approval employee (no required employee)
                  approvalInfo = (
                    <span>
                      Approved by <strong>{approvalEmployeeName}</strong> at <strong>{formattedDateTime}</strong>
                    </span>
                  );
                }
              } else if (!isApproved && approval.requiredEmployee && approval.requiredEmployee.name) {
                // This is a pending approval
                employeeName = `${approval.requiredEmployee.name.first} ${approval.requiredEmployee.name.last}`;
                approvalInfo = <strong>{employeeName}</strong>;
                
                // Determine if checkbox should be shown based on user role
                shouldShowCheckbox = 
                  userRole.admin || 
                  userRole.payroll || 
                  (userRole.supervisor && approval.requiredEmployee.adminEmployeeID === userID);
              } else {
                // Handle case where requiredEmployee doesn't exist
                approvalInfo = <span>Approval information unavailable</span>;
              }
             
             return (
               <div key={index} style={{
                 display: 'flex',
                 alignItems: 'center',
                 gap: '10px',
                 padding: '10px 0'
               }}>
                 {isApproved ? (
                   <span style={{
                     color: Colors.Warranty,
                     fontSize: '20px',
                     fontWeight: 'bold',
                     width: '20px'
                   }}>
                     ✓
                   </span>
                 ) : shouldShowCheckbox ? (
                   <input
                     type="checkbox"
                     checked={selectedSuperApprovals.includes(index)}
                     onChange={(e) => handleSuperApprovalCheckboxChange(index, e.target.checked)}
                     style={{
                       width: '20px',
                       height: '20px',
                       cursor: 'pointer'
                     }}
                   />
                 ) : (
                   <div style={{
                     width: '20px',
                     height: '20px',
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center'
                   }}>
                     {/* Empty space to align with checkmark above */}
                   </div>
                 )}
                 <span style={{
                   fontSize: '16px',
                   color: Colors.PrimaryText
                 }}>
                   {approvalInfo}
                 </span>
               </div>
             );
           })}
                 </div>
       )}

             {/* Required Payroll Approvals Section - Show for PayApproved with payrollApprovals for display only */}
       {type === TimesheetType.PayApproved && employee.payrollApprovals && employee.payrollApprovals.length > 0 && (
         <div className="approval-section" style={{
           backgroundColor: Colors.White,
           border: `1px solid ${Colors.BorderGray}`,
           borderRadius: '8px',
           padding: '20px',
           margin: '20px 0',
           boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
         }}>
           <div style={{
             display: 'flex',
             justifyContent: 'space-between',
             alignItems: 'center',
             marginBottom: '15px'
           }}>
             <h3 style={{
               margin: '0',
               color: Colors.PrimaryText,
               fontSize: '18px',
               fontWeight: '600'
             }}>
               Required Payroll Approvals
             </h3>
           </div>
           {employee.payrollApprovals && employee.payrollApprovals.map((approval: any, index: number) => {
                                         // Check if this is an approved approval (has approvalEmployee with required properties)
              const isApproved = approval.approvalEmployee && 
                                approval.approvalEmployee.name &&
                                approval.approvalDateTime;
              
              let approvalInfo: string | JSX.Element = '';
              
              if (isApproved && approval.approvalEmployee) {
                // This is an approved approval
                const approvalEmployeeName = `${approval.approvalEmployee.name.first} ${approval.approvalEmployee.name.last}`;
                const formattedDateTime = applyOffsetToUTCDateTime(
                  approval.approvalDateTime, 
                  approval.approvalDateTimeOffset
                );
                approvalInfo = (
                  <span>
                    Approved by <strong>{approvalEmployeeName}</strong> at <strong>{formattedDateTime}</strong>
                  </span>
                );
              }
             
             return (
               <div key={index} style={{
                 display: 'flex',
                 alignItems: 'center',
                 gap: '10px',
                 padding: '10px 0'
               }}>
                 {isApproved ? (
                   <span style={{
                     color: Colors.Warranty,
                     fontSize: '20px',
                     fontWeight: 'bold',
                     width: '20px'
                   }}>
                     ✓
                   </span>
                 ) : (
                   <div style={{
                     width: '20px',
                     height: '20px',
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center'
                   }}>
                     {/* Empty space to align with checkmark above */}
                   </div>
                 )}
                 <span style={{
                   fontSize: '16px',
                   color: Colors.PrimaryText
                 }}>
                   {approvalInfo}
                 </span>
               </div>
             );
           })}
         </div>
       )}

      <div className="header-container">
       <div className="top-row">
         <span>{infoStr} <strong>{employee.altEmployeeID}</strong></span>

                    <div className="buttons-container">
             <div style={{display: "flex", gap: "5px"}}>
             {type !== TimesheetType.Unsubmitted && 
              (!(type === TimesheetType.SuperApproved || type === TimesheetType.PayApproved) || 
               (userRole.admin || userRole.payroll)) && (
               <ActionButton
                 title={translator.translate("Unlock")}
                 isEnabled={true}
                 onAction={onUnlock}
               />
             )}
             {(type === TimesheetType.Submitted || (type === TimesheetType.SuperApproved && (userRole.admin || userRole.payroll))) && 
              !(type === TimesheetType.Submitted && employee.superApprovals && employee.superApprovals.length > 0) && (
               <ActionButton
                 title={translator.translate("Approve")}
                 isEnabled={true}
                 onAction={onApprove}
               />
             )}
             </div>
            
            <div style={{display: "flex", gap: "5px"}}>
              {type !== 'unsubmitted' && (
                <ActionButton
                  title="Print"
                  isEnabled={true}
                  onAction={onPrint}
                />
              )}
              
              <ActionButton
                title="Audit"
                isEnabled={true}
                onAction={onAudit}
              />
            </div>
            
            {/* Change Supervisor button */}
            {(type === TimesheetType.Submitted || type === TimesheetType.SuperApproved) && (userRole.payroll || userRole.admin) && (
              <div style={{display: "flex", gap: "5px"}}>
                <ActionButton
                  title="Change Supervisor"
                  isEnabled={true}
                  onAction={() => setChangeSupervisorModalOpen(true)}
                />
              </div>
            )}
          </div>
        </div>

        <div className="middle-row">
          <div className="middle-row-info-left">
            <span>Phone Number: {employee.phoneNumber ? formatPhoneNumber(employee.phoneNumber) : "-"}</span>
            <span>Rate: ${employee.hourlyRate ?? "(N/A)"}/hr</span>
            <span>Job Title: {jobTitle === "" ? "N/A" : jobTitle}</span>
          </div>
          <div className="middle-row-info-right">
            <span>Supervisor: {superName}</span>
            <span>Week {week}</span>
          </div>
        </div>
        
        {type !== 'unsubmitted' && (
          <div className="week-summary-container">
            <div className="timesheet-summary-container">
              <p>Regular: {formatTime(totalReg)}</p>
              <p>Overtime: {formatTime(totalOT)}</p>
              <p>Double Time: {formatTime(totalDT)}</p>
            </div>
          </div>
        )}
      </div>

      <div style={{gap: '50px', justifyContent: 'center', width: '97%', display: "flex", marginTop: '25px', padding: '0px 0px'}}>
      <span>
        <strong>Total Time:</strong> {
          formatTime(
            type === "unsubmitted"
              ? days.reduce((acc: number, day: any) => acc + (day.totalWorkMinutes ?? 0), 0)
              : totalDT + totalOT + totalReg +
                days.reduce((acc: number, day: any) => {
                  const sickTime = day.employeeSickClaims?.reduce(
                    (sum: number, claim: { minutesClaimed: number }) => sum + claim.minutesClaimed,
                    0
                  ) ?? 0;
                  return acc + sickTime;
                }, 0)
          )
        }
      </span>
          <span><strong>Total Amount:</strong> {DollarUtil.formatDollar(Number(getTotalAmountForWeek(employee.days).toFixed(2)))}</span>
          <span><strong>Total Tasks:</strong> {getTotalTaskCountForWeek(employee.days)}</span>
          {config.showAvgHourlyRate && (
            <span><strong>Avg Hourly Rate:</strong> {getAvgHourlyRate(employee.days)}</span>
          )}
        </div>
      

      {/* Table with Date, Weekday, and Hours Worked in a Row */}
      {config.showTaskAndHourlyMinutes ?
        <div className="timesheet-table-container">
          {/* Label Column */}
          <div className="timesheet-table-item timesheet-label-column">
            <div className="table-cell"></div>
            <div className="table-cell"></div>
            <div className="table-cell">{translator.translate("PieceWarehouse")}</div>
            <div className="table-cell">Hourly (Job)</div>
            {type !== 'unsubmitted' && <div className="table-cell">Warnings</div>}
            {!config.combineTasksInWeek ? <div className="table-cell"></div> : <></>}
          </div>
          {/* Day Columns */}
          {days.map((day: any, index: number) => {
            const hasSickClaims = day.employeeSickClaims && day.employeeSickClaims.length > 0;
            const sickMinutes = day.employeeSickClaims?.reduce((sum: any, claim: {minutesClaimed: any;}) => sum + claim.minutesClaimed, 0) ?? 0;
            const hasMealPenalty = type !== 'unsubmitted' && (day.fiveHourLunchException || day.fullLunchException || day.noLunchException);
            const hasTimeGapIssue = type !== 'unsubmitted' && hasTimeGap(day);
            const warnings: string[] = [];
            if (hasMealPenalty && config.showMealPenaltyWarning) warnings.push('Meal Penalty');
            if (hasTimeGapIssue) warnings.push('Time Gap');
            const { pieceMinutes, hourlyJobMinutes } = config.use2020PieceHourlyCalculation
              ? get2020PieceAndHourlyMinutesForDay(day)
              : { pieceMinutes: day.totalTaskMinutes ?? 0, hourlyJobMinutes: day.totalHourlyMinutes ?? 0 };
            return (
              <div key={index} className="timesheet-table-item">
                <div className="table-cell">{getShortDate(day.date).slice(0, 5)}</div>
                <div className="table-cell">{getWeekDay(day.date).slice(0, 3)}</div>
                <div className="table-cell" style={hasSickClaims ? { backgroundColor: '#27C2F1' } : {}}>
                  {formatTime(pieceMinutes + sickMinutes)}
                  {hasSickClaims && " (Sick)"}
                </div>
                <div className="table-cell">{formatTime(hourlyJobMinutes)}</div>
                {type !== 'unsubmitted' && (
                  <div className="table-cell" style={{ color: 'red', fontWeight: 'bold', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {warnings.map((warning, warningIndex) => (
                      <span
                        key={warningIndex}
                        onClick={() => scrollToDay(day.date)}
                        style={{ 
                          cursor: 'pointer', 
                          textDecoration: 'underline'
                        }}
                        title={`Click to scroll to ${getWeekDay(day.date)} - ${getDate(day.date)}`}
                      >
                        {warning}
                      </span>
                    ))}
                  </div>
                )}
                {!config.combineTasksInWeek ? <div className="table-cell">{DollarUtil.formatDollar(Number(getTotalProjectClaimedForDay(day).toFixed(2)))}</div> : <></>}
              </div>
            );
          })}
        </div>
        :
        <div className="timesheet-table-container">
          {days.map((day: any, index: number) => {
            const hasSickClaims = day.employeeSickClaims && day.employeeSickClaims.length > 0;
            return (
              <div key={index} className="timesheet-table-item">
                <div className="table-cell">{getShortDate(day.date).slice(0, 5)}</div>
                <div className="table-cell">{getWeekDay(day.date).slice(0, 3)}</div>
                <div className="table-cell" style={hasSickClaims ? { backgroundColor: '#27C2F1' } : {}}>
                  {formatTime(day.totalWorkMinutes + (day.employeeSickClaims?.reduce((sum: any, claim: {minutesClaimed: any;}) => sum + claim.minutesClaimed, 0) ?? 0))}
                  {hasSickClaims && " (Sick)"}
                </div>
                {!config.combineTasksInWeek ? <div className="table-cell">{DollarUtil.formatDollar(Number(getTotalProjectClaimedForDay(day).toFixed(2)))}</div> : <></>}
              </div>
            );
          })}
        </div>
      }

      {/* View Mode Toggle */}
      <div className="view-mode-toggle-container">
        <div className="task-toggle-switch">
          <input 
            type="checkbox" 
            id="task-view-toggle" 
            checked={viewMode === 'tasks'} 
            onChange={toggleViewMode}
          />
          <label htmlFor="task-view-toggle" className="task-slider">
            <span className="task-hours-text">Hours</span>
            <span className="task-tasks-text">{translator.translate("Tasks")}</span>
            <span className="task-slider-button"></span>
          </label>
        </div>
      </div>

      {/* Legend Section */}
      {viewMode === 'hours' ? 
        <>
          <div className="legend-container">
            <div className="legend-item">
              <div className="legend-square" style={{ backgroundColor: Colors.LightBlue }}></div>
              <span>{translator.translate("Regular")}</span>
            </div>
            <div className="legend-item">
              <div className="legend-square" style={{ backgroundColor: Colors.Edited }}></div>
              <span>{translator.translate("EditedTime")}</span>
            </div>
            <div className="legend-item">
              <div className="legend-square" style={{ backgroundColor: Colors.Added }}></div>
              <span>{translator.translate("AddedTime")}</span>
            </div>
          </div>
        </> :
        <>
          <div className="legend-container">
            <div className="legend-item">
              <div className="legend-square" style={{ backgroundColor: Colors.Misc }}></div>
              <span>= Misc</span>
            </div>
            <div className="legend-item">
              <div className="legend-square" style={{ backgroundColor: Colors.Sick }}></div>
              <span>= Sick</span>
            </div>
            <div className="legend-item">
              <div className="legend-square" style={{ backgroundColor: Colors.Warranty }}></div>
              <span>= Warranty</span>
            </div>
            <div className="legend-item">
              <div className="legend-square" style={{ backgroundColor: Colors.Operational }}></div>
              <span>= Meetings / Other</span>
            </div>
          </div>
          {/* SuperApproved: payroll enters the rate, with the 13-week lookback offered as a
              suggestion. PayApproved: read-only comparison of the saved rate against the
              computed one, so payroll can go back and validate prior weeks. */}
          {(type === TimesheetType.SuperApproved || type === TimesheetType.PayApproved) &&
            !config.hideSickClaimHourlyRate &&
            hasAnySickClaims && (
              <div
                style={{
                  width: "95%",
                  justifyContent: "center",
                  display: "flex",
                  marginTop: "15px",
                }}
              >
                <TaskSetter
                  employeeSickClaimIDs={sickClaims}
                  initialAmount={savedSickRate}
                  readOnly={type === TimesheetType.PayApproved}
                />
              </div>
            )}
        </>
    }
      
      
      <div>
        {days.length > 0 ? (
          days.map((day: Day, index: number) => {
            const hasTimeEntries = day.timeEntries.length > 0;
            const hasProjects = day.hourlyProjects && day.hourlyProjects.length > 0;
            let hasTasks = day.projectEmployeeTasks && day.projectEmployeeTasks.length > 0
             || day.employeeSickClaims && day.employeeSickClaims.length > 0
             || day.employeeCustomerServiceWarrantyTasks && day.employeeCustomerServiceWarrantyTasks.length > 0
             || day.employeeOperationalTasks && day.employeeOperationalTasks.length > 0
             || day.employeeWorkOrderTasks && day.employeeWorkOrderTasks.length > 0;

             // If combineTasksInWeek is true, then we need to check if the day has any sick claims
             if (config.combineTasksInWeek) {
              hasTasks = day.employeeSickClaims && day.employeeSickClaims.length > 0;
             }

            if ((hasTimeEntries || hasProjects) && viewMode === 'hours') {
              const hasMealPenalty = type !== 'unsubmitted' && (day.fiveHourLunchException || day.fullLunchException || day.noLunchException);
              const hasTimeGapIssue = type !== 'unsubmitted' && hasTimeGap(day);
              const warnings: string[] = [];
              if (hasMealPenalty && config.showMealPenaltyWarning) warnings.push('Meal Penalty');
              if (hasTimeGapIssue) warnings.push('Time Gap');
              return (
                <div key={index} id={`day-${day.date}`} className="time-entry-day-box">
                  <div className="day-summary-container">
                    <div className="summary-item" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', flexWrap: 'nowrap', flexShrink: 0 }}>
                      <p style={{ margin: 0, flexShrink: 0, whiteSpace: 'nowrap' }}><strong>{getWeekDay(day.date)} - {getDate(day.date)}</strong></p>
                      {dayHasValidGps(day) && (
                        <button
                          type="button"
                          className="gps-map-link-button"
                          onClick={() => openDayMap(day)}
                          title="View day punches on map"
                          style={{ marginLeft: 15, flexShrink: 0 }}
                        >
                          <MapIcon />
                          Map
                        </button>
                      )}
                    </div>
                    {warnings.length > 0 && (
                      <div className="summary-item" style={{ justifyContent: 'center' }}>
                        <div className="meal-penalty-indicator" style={{ display: 'flex', flexDirection: 'column', gap: '4px', justifyContent: 'center' }}>
                          {warnings.map((warning, warningIndex) => (
                            <strong key={warningIndex}>{warning}</strong>
                          ))}
                        </div>
                      </div>
                    )}
                    {type !== 'unsubmitted' && (
                      <>
                        <div className="summary-item">
                          <p><strong>REG:</strong> {formatTime(day.regularTime)}</p>
                        </div>
                        <div className="summary-item">
                          <p><strong>OT:</strong> {formatTime(day.overTime)}</p> 
                        </div>
                        <div className="summary-item">
                          <p><strong>DT:</strong> {formatTime(day.doubleTime)}</p> 
                        </div>
                      </>
                    )}
                    <div className="summary-item">
                      <p><strong>{translator.translate("TotalHours")}</strong> {formatTime(day.totalWorkMinutes)}</p> 
                    </div>
                  </div>

                  {/* Regular Time Entries */}
                  {hasTimeEntries && (() => {
                    const gapTimes = type !== 'unsubmitted' ? getTimeGapEntries(day) : new Set<string>();
                    return (
                      <div className="time-entry-cards">
                        {day.timeEntries.map((entry: any, entryIndex: number) => {
                          const entryIdentifier = `timeEntry-${entryIndex}`;
                          const highlightIn = gapTimes.has(`${entryIdentifier}-in`);
                          const highlightOut = gapTimes.has(`${entryIdentifier}-out`);
                          return (
                            <div 
                              key={entryIndex} 
                              className="time-entry-card" 
                              style={{
                                backgroundColor: `${getTimeType(entry.type)}`
                              }}
                            >
                              <div className="time-entry-row">
                                <div className="time-entry-in-out-wrapper">
                                  <div 
                                    className="time-entry-in-out-container"
                                    style={{
                                      backgroundColor: `${getPunchType(entry.punchTypeIn)}`,
                                      border: highlightIn ? '3px solid red' : undefined
                                    }}
                                  >
                                    <div className="time-label">
                                      <strong>{translator.translate("In")}</strong> {applyOffsetToUTC(entry.inDateTime, entry.inDateTimeOffset)}
                                    </div>
                                  </div>
                                  {hasValidGps(entry.inLatitude, entry.inLongitude) && (
                                    <button
                                      type="button"
                                      className="gps-pin-button"
                                      onClick={() => openEntryMap(entry, `Punch ${entryIndex + 1}`, "in")}
                                      title="View In location on map"
                                    >
                                      <GpsPinIcon isException={!!entry.gpsException?.inException} />
                                    </button>
                                  )}
                                </div>
                                <div className="time-entry-in-out-wrapper">
                                  <div 
                                    className="time-entry-in-out-container"
                                    style={{
                                      backgroundColor: `${getPunchType(entry.punchTypeOut)}`,
                                      border: highlightOut ? '3px solid red' : undefined
                                    }}
                                  >
                                    <div className="time-label">
                                      <strong>{translator.translate("Out")}</strong> {entry.outDateTime ? applyOffsetToUTC(entry.outDateTime, entry.outDateTimeOffset) : ""}
                                    </div>
                                  </div>
                                  {hasValidGps(entry.outLatitude, entry.outLongitude) && (
                                    <button
                                      type="button"
                                      className="gps-pin-button"
                                      onClick={() => openEntryMap(entry, `Punch ${entryIndex + 1}`, "out")}
                                      title="View Out location on map"
                                    >
                                      <GpsPinIcon isException={!!entry.gpsException?.outException} />
                                    </button>
                                  )}
                                </div>
                              </div>
                              <div className="total-hours-container">
                                  <p><strong>{translator.translate("TotalHours")}</strong> {getTimeDiff(entry.inDateTime, entry.outDateTime)}</p>
                              </div>
                              <div className="total-hours-container">
                                  <p><strong>{translator.translate("Type")}</strong> {TimeEntryType[entry.type]}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}

                  {/* Project Time Entries */}
                  {hasProjects && (
                    <div className="project-entries-container">
                      <h4 className="project-entries-header">{translator.translate("ProjectTime")}</h4>
                      {day.hourlyProjects.map((project: any, projectIndex: number) => (
                        <div key={projectIndex} className="project-entry">
                          <div className="hourly-project-info">
                            <div style={{width: '100%'}}>
                              {project.customerServiceWarranty && project.customerServiceWarranty.name && (
                                <div>
                                  <strong>{project.customerServiceWarranty.name}</strong>
                                </div>
                              )}
                              {project.project && project.project.name && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <strong>{project.project.name}{project.project.altProjectID ? ` - ${project.project.altProjectID}` : ''}</strong>
                                  {hasValidGps(project.project.latitude, project.project.longitude) && typeof project.project.radiusMiles === 'number' && project.project.radiusMiles > 0 && (
                                    <button
                                      type="button"
                                      className="gps-map-link-button"
                                      onClick={() => openProjectMap(project, day)}
                                      title="View project and punch locations on map"
                                    >
                                      <MapIcon />
                                      Map
                                    </button>
                                  )}
                                </div>
                              )}
                              {project.locationName && (
                                <div style={{ marginLeft: '10px' }}>
                                  Lot: {project.locationName}
                                </div>
                              )}
                              {project.description && (
                                <div style={{ marginLeft: '10px' }}>
                                  Description: {project.description}
                                </div>
                              )}
                              {project.projectPhase && project.projectPhase.name && (
                                <div style={{ fontWeight: 500, fontSize: '0.95em', marginLeft: '10px', marginTop: '5px', marginBottom: '5px' }}>
                                  <strong>{project.projectPhase.name}{project.projectPhase.altProjectPhaseID ? ` - ${project.projectPhase.altProjectPhaseID}` : ''}</strong>
                                </div>
                              )}
                              {project.account && project.account.name && (
                                <div style={{ fontWeight: 500, fontSize: '0.95em', marginLeft: '10px' }}>
                                  <strong>{project.account.name} - {project.account.altAccountCodeID}</strong>
                                </div>
                              )}
                              {project.serviceOrderText && (
                                <div style={{ fontWeight: 500, fontSize: '0.95em' }}>
                                  <strong>S.O. #{project.serviceOrderText}</strong>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="time-entry-cards">
                            {(() => {
                              const gapTimes = type !== 'unsubmitted' ? getTimeGapEntries(day) : new Set<string>();
                              return project.timeEntriesHourly.map((entry: any, entryIndex: number) => {
                                const entryIdentifier = `hourly-${projectIndex}-${entryIndex}`;
                                const highlightIn = gapTimes.has(`${entryIdentifier}-in`);
                                const highlightOut = gapTimes.has(`${entryIdentifier}-out`);
                                return (
                                  <div 
                                    key={entryIndex} 
                                    className="time-entry-card" 
                                    style={{
                                      backgroundColor: `${getTimeType(entry.type)}`
                                    }}
                                  >
                                    <div className="time-entry-row">
                                      <div className="time-entry-in-out-wrapper">
                                        <div 
                                          className="time-entry-in-out-container"
                                          style={{
                                            backgroundColor: `${getPunchType(entry.punchTypeIn)}`,
                                            border: highlightIn ? '3px solid red' : undefined
                                          }}
                                        >
                                          <div className="time-label">
                                            <strong>{translator.translate("In")}</strong> {applyOffsetToUTC(entry.inDateTime, entry.inDateTimeOffset)}
                                          </div>
                                        </div>
                                        {hasValidGps(entry.inLatitude, entry.inLongitude) && (
                                          <button
                                            type="button"
                                            className="gps-pin-button"
                                            onClick={() =>
                                              openEntryMap(
                                                entry,
                                                `Punch ${entryIndex + 1}`,
                                                "in",
                                                project.project,
                                                project.project
                                                  ? `${project.project.name ?? ""}${project.project.altProjectID ? ` - ${project.project.altProjectID}` : ""}`.trim()
                                                  : undefined
                                              )
                                            }
                                            title="View In location on map"
                                          >
                                            <GpsPinIcon isException={!!entry.gpsException?.inException} />
                                          </button>
                                        )}
                                      </div>
                                      <div className="time-entry-in-out-wrapper">
                                        <div 
                                          className="time-entry-in-out-container"
                                          style={{
                                            backgroundColor: `${getPunchType(entry.punchTypeOut)}`,
                                            border: highlightOut ? '3px solid red' : undefined
                                          }}
                                        >
                                          <div className="time-label">
                                            <strong>{translator.translate("Out")}</strong> {entry.outDateTime ? applyOffsetToUTC(entry.outDateTime, entry.outDateTimeOffset) : ""}
                                          </div>
                                        </div>
                                        {hasValidGps(entry.outLatitude, entry.outLongitude) && (
                                          <button
                                            type="button"
                                            className="gps-pin-button"
                                            onClick={() =>
                                              openEntryMap(
                                                entry,
                                                `Punch ${entryIndex + 1}`,
                                                "out",
                                                project.project,
                                                project.project
                                                  ? `${project.project.name ?? ""}${project.project.altProjectID ? ` - ${project.project.altProjectID}` : ""}`.trim()
                                                  : undefined
                                              )
                                            }
                                            title="View Out location on map"
                                          >
                                            <GpsPinIcon isException={!!entry.gpsException?.outException} />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                    <div className="total-hours-container">
                                        <p><strong>{translator.translate("TotalHours")}</strong> {getTimeDiff(entry.inDateTime, entry.outDateTime)}</p>
                                    </div>
                                    <div className="total-hours-container">
                                        <p><strong>{translator.translate("Type")}</strong> {TimeEntryType[entry.type]}</p>
                                    </div>
                                  </div>
                                );
                              });
                            })()}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            } else if (hasTasks && viewMode === 'tasks') {

              if (config.combineTasksInWeek) {
                return (
                  <>
                    <div key={index} id={`day-${day.date}`} className="time-entry-day-box">
                      <div className="day-summary-container">
                        <div className="summary-item">
                          <p><strong>{getWeekDay(day.date)} - {getDate(day.date)}</strong></p> 
                        </div>
                        {day.employeeSickClaims.length > 0 ? <p><strong>Total Hours:</strong> {formatTime(day.employeeSickClaims?.reduce((sum: any, claim: {minutesClaimed: any;}) => sum + claim.minutesClaimed, 0) ?? 0)}</p>
                        : 
                        <></>
                        }
                      </div>

                      {/* Task Entries */}
                      <div className="project-entries-container">

                        {/* Sick Claims */}
                        {renderSickClaims(day.employeeSickClaims)}

                      </div>
                    </div>
                  </>
                );
              }
              else {

                return (

                  <>
                  <div key={index} id={`day-${day.date}`} className="time-entry-day-box">
                    <div className="day-summary-container">
                      <div className="summary-item">
                        <p><strong>{getWeekDay(day.date)} - {getDate(day.date)}</strong></p> 
                      </div>
                      {day.employeeSickClaims.length > 0 ? <p><strong>Total Hours:</strong> {formatTime(day.employeeSickClaims?.reduce((sum: any, claim: {minutesClaimed: any;}) => sum + claim.minutesClaimed, 0) ?? 0)}</p>
                      : 
                      <></>
                      }
                      <div className="summary-item">
                        <p><strong>Total Claimed:</strong> {DollarUtil.formatDollar(Number(getTotalProjectClaimedForDay(day).toFixed(2)))}</p>
                      </div>
                    </div>

                    {/* Task Entries */}
                    <div className="project-entries-container">
                      {renderProjectTasks(day.projectEmployeeTasks, canShowMiscUpdate)}

                      {/* Sick Claims */}
                      {renderSickClaims(day.employeeSickClaims)}

                      {/* Meetings / Other Tasks */}
                      {renderOperationalTasks(day.employeeOperationalTasks)}

                      {/* Customer Warranties */}
                      {renderCustomerWarranties(day.employeeCustomerServiceWarrantyTasks)}

                      {/* Work Orders */}
                      {renderWorkOrderTasks(day.employeeWorkOrderTasks ?? [])}
                    </div>
                  </div>
                  </>
                );
              }
            }
            return null;
          })
        ) : (
          <p className="no-time-entries-message">{translator.translate("NoTimeEntries")}</p>
        )}
        {
          config.combineTasksInWeek && viewMode === 'tasks' && (
            <div className="time-entry-day-box">
              <div className="day-summary-container">
                <div className="summary-item">
                  <p><strong>Weekly Tasks Summary</strong></p>
                </div>
                <div className="summary-item">
                  <p><strong>Total Claimed:</strong> {DollarUtil.formatDollar(Number(getTotalAmountForWeek(days).toFixed(2)))}</p>
                </div>
              </div>

              <div className="project-entries-container">
                {renderProjectTasks(aggregateWeeklyProjectTasks(days), false)}
              </div>
            </div>
          )
        }
      </div>
      <AuditDetailModal
        isOpen={auditModalOpen}
        onClose={() => setAuditModalOpen(false)}
        auditData={auditData}
      />
      <ChangeSupervisorModal
        visible={changeSupervisorModalOpen}
        onClose={() => setChangeSupervisorModalOpen(false)}
        employeeName={empName}
        employeeAltID={empID.toString()}
        currentSupervisorName={superName || "Unknown"}
        currentSupervisorAltID={superAltID || "Unknown"}
        timesheetID={parseInt(timesheetID!, 10)}
        userID={userID}
      />
      <GpsMapModal
        isOpen={gpsMapOpen}
        onClose={() => setGpsMapOpen(false)}
        title={gpsMapTitle}
        points={gpsMapPoints}
        circles={gpsMapCircles}
        showRadiusLegend={gpsMapShowRadiusLegend}
        showPunchList={gpsMapShowPunchList}
      />
      {miscEditOpen && miscEditTarget && (
        <UpdateMiscEmployeeTaskModal
          key={miscEditTarget.miscEmployeeTaskID}
          open
          onClose={() => {
            setMiscEditOpen(false);
            setMiscEditTarget(null);
          }}
          miscEmployeeTaskID={miscEditTarget.miscEmployeeTaskID}
          initialAccountCodeID={miscEditTarget.initialAccountCodeID}
          initialMiscDescription={miscEditTarget.initialMiscDescription}
          onSuccess={onRefresh}
        />
      )}
    </PageWrapper>
  );
};

export default TimesheetPage;